import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, TriangleAlert, CircleAlert, OctagonAlert, ShieldAlert, Shield, ArrowLeftRight } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

const RISK_CONFIG = {
    Critical: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        badge: 'bg-red-600 text-white',
        icon: CircleAlert
    },
    High: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        badge: 'bg-orange-500 text-white',
        icon: TriangleAlert
    },
    Medium: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        badge: 'bg-yellow-400 text-yellow-900',
        icon: OctagonAlert
    },
    Low: {
        bg: 'bg-white',
        border: 'border-base-300',
        badge: 'bg-gray-200 text-gray-700',
        icon: ShieldAlert
    }
};

const DriftDetectorTab = ({ scanId }) => {
    const [driftData, setDriftData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cached, setCached] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [tokensUsed, setTokensUsed] = useState(null);
    const [costUsd, setCostUsd] = useState(null);

    useEffect(() => {
        const fetchCachedDriftData = async () => {
            setLoading(true);
            setError(null);
            setCached(false);
            try {
                const cachedResp = await fetch(ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Drift Detector"), { method: 'POST', credentials: 'include' });
                if (cachedResp.status === 200) {
                    const cachedJson = await cachedResp.json();
                    if (cachedJson && cachedJson.response) {
                        const parsed = JSON.parse(cachedJson.response);
                        setDriftData(parsed);
                        setCached(true);
                        setLastUpdated(cachedJson.lastUpdated || null);
                        setTokensUsed(cachedJson.tokens_used || null);
                        setCostUsd(cachedJson.cost_usd || null);
                        setLoading(false);
                        return;
                    }
                }
                setDriftData(null);
                setCached(false);
            } catch (err) {
                setDriftData(null);
                setCached(false);
            } finally {
                setLoading(false);
            }
        };
        fetchCachedDriftData();
    }, [scanId]);

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${ApiNames.AI_HUB_DRIFT_DETECTOR(scanId)}`, { method: 'POST', credentials: 'include' });
            if (!response.ok) throw new Error('Failed to fetch drift data');
            const json = await response.json();
            const parsed = JSON.parse(json.response);
            setDriftData(parsed);
            setCached(false);
            setLastUpdated(json.lastUpdated || null);
            setTokensUsed(json.tokens_used || null);
            setCostUsd(json.cost_usd || null);
        } catch (err) {
            setError('Failed to load drift detector data.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="flex flex-col items-center gap-8">
            <div className="w-full max-w-6xl">
                <div className="card-header flex items-center justify-between mb-2">
                    <div className="card-title flex items-center gap-2">
                        <Shield className="w-6 h-6 text-blue-400" />
                        <h3 className="font-bold text-lg">AI Drift Detector</h3>
                    </div>
                    {driftData ? (
                        <span className="card-badge flex items-center gap-1 bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-semibold">
                            <TriangleAlert className="w-4 h-4 mr-1" />{driftData['Written Policy'].length} Drifts Detected
                        </span>
                    ) : (
                        <span className="card-badge flex items-center gap-1 bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-semibold">
                            <TriangleAlert className="w-4 h-4 mr-1" />No Drifts Detected Yet
                        </span>
                    )}
                </div>
                <p className="text-gray-400 mb-6 text-sm mt-2">
                    Detects and explains gaps between written policy and technical reality. Use this to identify compliance drift and prioritize remediation.
                </p>
                {loading && (
                    <div className="ai-response flex flex-col items-center justify-center py-12 animate-fadeIn">
                        <Loader2 className="animate-spin text-blue-400 w-10 h-10 mb-4" />
                        <span className="text-gray-400">Generating AI analysis...</span>
                    </div>
                )}
                {!loading && error && (
                    <div className="ai-response text-center text-red-400 py-12">{error}</div>
                )}
                {!loading && !error && driftData && (
                    <>
                        <div className="ai-response bg-base-100 border border-base-300 rounded-lg p-4 animate-fadeIn mb-2">
                            {/* Header Titles for the Grid */}
                            <div className="grid grid-cols-[1fr_2rem_1fr] gap-3 mb-2 px-1">
                                <h4 className="font-bold text-sm flex items-center gap-2">
                                    <span className="w-1.5 h-4 bg-primary rounded-full"></span>
                                    Written Policy
                                </h4>
                                <div></div> {/* Spacer for arrow column */}
                                <h4 className="font-bold text-sm flex items-center gap-2">
                                    <span className="w-1.5 h-4 bg-secondary rounded-full"></span>
                                    Technical Reality
                                </h4>
                            </div>

                            {/* Main Comparison Grid */}
                            <div className="grid grid-cols-[1fr_2rem_1fr] gap-x-3 gap-y-2 items-stretch mb-6">
                                {Array.from({ length: Math.max(driftData['Written Policy']?.length || 0, driftData['Technical Reality']?.length || 0) }).map((_, idx) => (
                                    <React.Fragment key={idx}>
                                        {/* Policy Card */}
                                        <div className="border border-base-300 bg-base-50 p-3 rounded-lg shadow-sm flex flex-col justify-center">
                                            {driftData['Written Policy']?.[idx] ? (
                                                <>
                                                    <span className="font-bold text-primary text-[11px] uppercase mb-1">
                                                        {driftData['Written Policy'][idx].Control}
                                                    </span>
                                                    <p className="text-xs text-base-content/80 leading-snug italic">
                                                        "{driftData['Written Policy'][idx].Language}"
                                                    </p>
                                                </>
                                            ) : <div className="h-full bg-base-200/20 rounded border-dashed border border-base-300" />}
                                        </div>

                                        {/* Arrow Icon */}
                                        <div className="flex items-center justify-center">
                                            <ArrowLeftRight className="text-gray-300 w-4 h-4" />
                                        </div>

                                        {/* Reality Card */}
                                        <div className="border border-base-300 bg-base-50 p-3 rounded-lg shadow-sm flex flex-col justify-center">
                                            {driftData['Technical Reality']?.[idx] ? (
                                                <div className="flex flex-row gap-x-1">
                                                    <p className="text-xs font-semibold text-base-content/80 leading-snug">Observed:</p>
                                                    <p className="text-xs text-base-content/80 leading-snug">
                                                        {driftData['Technical Reality'][idx]}
                                                    </p>
                                                </div>
                                            ) : <div className="h-full bg-base-200/20 rounded border-dashed border border-base-300" />}
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>

                            {/* Breakdown Section */}
                            <div className="mt-6">
                                <h4 className="font-bold text-sm mb-3">Breakdown</h4>
                                <div className="flex flex-col gap-2">
                                    {driftData['Breakdown']?.map((item, idx) => {
                                        const config = RISK_CONFIG[item.Severity] || RISK_CONFIG.Low;
                                        const Icon = config.icon;

                                        return (
                                            <div
                                                key={idx}
                                                className={`${config.bg} ${config.border} border p-3 rounded-lg shadow-sm`}
                                            >
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Icon className={`w-4 h-4 ${item.Severity === 'Low' ? 'text-gray-400' : ''}`} />
                                                    <span className="font-bold text-xs">
                                                        {item.Severity} {item.Area} Drift: {item.Control}
                                                    </span>
                                                </div>
                                                <div className="text-[11px] text-base-content/70 leading-normal border-t border-black/5 pt-2">
                                                    {item.Analysis}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Metadata footer */}
                            <div className="mt-6 pt-3 border-t border-base-200 text-[10px] text-gray-400 flex flex-wrap gap-4">
                                {tokensUsed && <span>Tokens used: {tokensUsed}. Cost: ${costUsd}</span>}
                                {cached && lastUpdated && (
                                    <span>Viewing Cached Drift Detector analysis. Last updated: {lastUpdated}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-row justify-center gap-4 mb-4">
                            <button
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150 flex items-center gap-2"
                                onClick={handleGenerate}
                                disabled={loading || !scanId}
                            >
                                <RefreshCw className="w-5 h-5" /> Regenerate Analysis
                            </button>
                        </div>
                    </>
                )}
                {!loading && !error && !driftData && !cached && (
                    <div className="flex flex-row justify-center gap-4 mb-4">
                        <button
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150 flex items-center gap-2"
                            onClick={handleGenerate}
                            disabled={loading || !scanId}
                        >
                            <RefreshCw className="w-5 h-5" /> Generate Analysis
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DriftDetectorTab;
